import { gql } from 'graphql-request'
import { z } from 'zod'
import { SearchForm } from '../components/SearchSideSheet/useSearchForm'
import { formatDateToShortFormat } from '../helpers/dateHelpers'
import { PassengersType } from '../helpers/searchQueryParams'
import { getVaaGQLClient } from './utils/vaaGQLClient'
import { flightFragment, flightSchema } from './models/flight'

export const CURRENCY_SYMBOLS = {
  USD: '$',
  GBP: '£',
}

export const farePriceCurrencySchema = z.enum(['USD', 'GBP'])

export type FarePriceCurrency = z.infer<typeof farePriceCurrencySchema>

const fareSchema = z.object({
  id: z.string().nullable(),
  fareFamilyType: z.enum(['AWARD-ECONOMY', 'AWARD-COMFORT-PLUS-PREMIUM-ECONOMY', 'AWARD-BUSINESS-FIRST']),
  availability: z.enum(['SOLD_OUT', 'NOT_OFFERED']).nullable(),
  availableSeatCount: z.number().nullable(),
  price: z
    .object({
      awardPoints: z.string(),
      awardPointsDifference: z.string().nullable(),
      awardPointsDifferenceSign: z.string().nullable(),
      amountIncludingTax: z.number(),
      currency: farePriceCurrencySchema,
    })
    .nullable(),
  fareSegments: z
    .array(
      z.object({
        cabinName: z.string(),
      })
    )
    .nullable(),
})

const flightAndFaresSchema = z.object({
  flight: flightSchema,
  fares: z.array(fareSchema),
})

export type Fare = z.infer<typeof fareSchema>
export type FlightAndFares = z.infer<typeof flightAndFaresSchema>
export type FareFamilyType = Fare['fareFamilyType']

const FlightSearchResultFragment = gql`
  fragment FlightSearchResultFragment on Result {
    slices {
      current
      total
    }
    slice {
      flightsAndFares {
        flight {
          ...FlightFragment
        }
        fares {
          id
          fareFamilyType
          availability
          availableSeatCount
          price {
            awardPoints
            awardPointsDifference
            awardPointsDifferenceSign
            amountIncludingTax
            currency
          }
          fareSegments {
            cabinName
          }
        }
      }
    }
    basketId
  }

  ${flightFragment(true)}
`

const flightSearchResultSchema = z.object({
  result: z.object({
    slices: z.object({
      current: z.number(),
      total: z.number(),
    }),
    slice: z.object({
      flightsAndFares: z.array(flightAndFaresSchema),
    }),
    basketId: z.string(),
  }),
})

export const generateCustomerDetailsRequestParam = (passengers: PassengersType) => {
  const customerDetailsMapping: Record<keyof PassengersType, string> = {
    adults: 'ADT',
    youngAdults: 'GBE',
    children: 'CNN',
    infant: 'INF',
  }

  return Object.entries(passengers).flatMap(([type, count]) => {
    const ptc = customerDetailsMapping[type as keyof PassengersType]

    return Array.from({ length: count }, (_, i) => ({
      custId: `${ptc}_${i}`,
      ptc,
    }))
  })
}

export async function getSearchFlights(form: SearchForm) {
  const { departure, destination, date, ...passengers } = form
  const customerDetails = generateCustomerDetailsRequestParam(passengers)

  const searchOriginDestinations = [
    {
      origin: departure?.code,
      destination: destination?.code,
      departureDate: formatDateToShortFormat(date.from),
    },
  ]

  if (date.to) {
    searchOriginDestinations.push({
      origin: destination?.code,
      destination: departure?.code,
      departureDate: formatDateToShortFormat(date.to),
    })
  }

  const result = await getVaaGQLClient().request({
    document: gql`
      query SearchOffers($request: FlightOfferRequestInput!) {
        searchOffers(request: $request) {
          result {
            ...FlightSearchResultFragment
          }
        }
      }

      ${FlightSearchResultFragment}
    `,
    variables: {
      request: {
        currencyCode: 'GBP', // effects tax currency in the search summary
        flightSearchRequest: {
          awardSearch: true,
          searchOriginDestinations,
          currentTripIndexId: '0',
        },
        customerDetails,
      },
    },
  })

  const flightSearchResponseSchema = z.object({
    searchOffers: flightSearchResultSchema,
  })

  const {
    searchOffers: {
      result: {
        slice: { flightsAndFares },
        basketId,
      },
    },
  } = flightSearchResponseSchema.parse(result)

  return {
    flightsAndFares,
    basketId,
  }
}

export async function getNextSearchFlights({ basketId: currentBasketId, offerId }: { basketId: string; offerId: string }) {
  const result = await getVaaGQLClient().request({
    document: gql`
      query SearchOffersNextSlice($request: SearchOffersNextSliceRequestInput!) {
        searchOffersNextSlice(request: $request) {
          result {
            ...FlightSearchResultFragment
          }
        }
      }

      ${FlightSearchResultFragment}
    `,
    variables: {
      request: {
        basketId: currentBasketId,
        offerId,
        // hardcoded for now given we only are doing round trips, will need to update if we support multi-city
        currentTripIndexId: 1,
      },
    },
  })
  const nextSearchFlightsResponseSchema = z.object({
    searchOffersNextSlice: flightSearchResultSchema,
  })

  const {
    searchOffersNextSlice: {
      result: {
        slice: { flightsAndFares },
        basketId,
      },
    },
  } = nextSearchFlightsResponseSchema.parse(result)

  return {
    flightsAndFares,
    basketId,
  }
}
